import { InlineLoading, Link } from '@carbon/react';
import { useTranslation, type I18nKey } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import { asProblem, problemMessage } from '../problems';
import { ordersKey, useFormat, useOrdersApi } from './model';

/**
 * A paid order's e-invoice as HO has it (ADR 0048, FR-EIN-04): HO issues it from the order the store sent, so the store asks
 * HO (3 seconds). Without HO it says so; nothing else on the page depends on it.
 */
export function OrderEinvoiceFact({ orderId }: { orderId: string }) {
  const { t } = useTranslation();
  const format = useFormat();
  const api = useOrdersApi();
  const einvoice = useQuery({
    queryKey: [...ordersKey, 'einvoice', orderId],
    queryFn: () => api.einvoice(orderId),
    retry: false,
    staleTime: 30_000,
  });

  if (einvoice.isLoading) return <InlineLoading description={t('orders.einvoice.loading')} />;
  if (einvoice.error) return <span>{problemMessage(t, asProblem(einvoice.error))}</span>;
  const data = einvoice.data;
  if (!data) return null;

  const status = t(`orders.einvoice.status.${data.status}` as I18nKey, { defaultValue: data.status });
  if (data.status !== 'issued') return <span>{status}</span>;
  return (
    <span>
      {[
        t('orders.einvoice.number', { number: data.invoiceNo ?? '' }),
        data.sellerTaxCode ? t('orders.einvoice.seller', { taxCode: data.sellerTaxCode }) : undefined,
        data.lookupCode ? t('orders.einvoice.lookupCode', { code: data.lookupCode }) : undefined,
        data.issuedAt ? format.dateTime(data.issuedAt) : undefined,
      ]
        .filter(Boolean)
        .join(' · ')}
      {data.lookupUrl && (
        <>
          {' '}
          <Link href={data.lookupUrl} target="_blank" rel="noopener noreferrer">
            {t('orders.einvoice.lookUp')}
          </Link>
        </>
      )}
    </span>
  );
}
